import {
  INavigationData,
  ITaskTypes,
  ITaskData,
  IInteractionTypes,
  IInteractionData,
  INavigationCardData,
} from "@origin-digital/reporting-client";
import { EnvironmentNames } from "@origin-digital/platform-enums";

import {
  AnalyticsEventType,
  BrazeEventType,
  InteractionDetailType,
  NavigationDetailType,
  NavigationCardDetailType,
  TaskDetailType,
} from "../consts/interfaces";
import {
  InteractionType,
  NavigationCardType,
  NavigationType,
  TaskType,
  WHITE_LIST_BRAZE,
} from "../consts/whitelistBraze";
import {
  EVENT_NAV_PREFIX,
  NAVIGATION_DOMAIN,
  PRODUCTION_DOMAIN_URI,
  PAC_DOMAIN_URI,
} from "./config";

export const shouldUseEventDispatch = (buttonLink: string) =>
  buttonLink && buttonLink.indexOf(EVENT_NAV_PREFIX) === 0;

// Converts event appName to app-name
export const setKebabCase = (str: string) => {
  return str.replace(/[A-Z]/g, (c) => {
    return `-${c.toLowerCase()}`;
  });
};

// Format to return currentUri (replace domain, remove parameters, and remove trailing slash)
export const formatUri = (
  environment: EnvironmentNames,
  currentUri: string
) => {
  // slice off either *origindigital-pac.com.au or *originenergy.com.au
  // and replace with NAVIGATION_DOMAIN
  const domain =
    environment === "prod" ? PRODUCTION_DOMAIN_URI : PAC_DOMAIN_URI;

  const domainCorrectedUri = currentUri.replace(
    new RegExp(`(.*)${domain}`, "g"),
    NAVIGATION_DOMAIN
  );
  // remove any parameters
  const removeParametersUri = domainCorrectedUri.split("?")[0];

  // remove trailing `/`
  return removeParametersUri?.replace(/\/*$/, "");
};

let brazeEventsToBeSent: BrazeEventType[] = [];

// Queue up events
export const addBrazeEventsToBeSent = (event: BrazeEventType) => {
  brazeEventsToBeSent.push(event);
};

// Filter on:
// event.detail.data.appName
// event.detail.type
// event.details.data.action
// <app-name>::task::<type>::<action>
export const getTaskEvent = (
  detail: {
    eventType: AnalyticsEventType;
    type: ITaskTypes;
    data: ITaskData;
  },
  whitelist: TaskType[]
): string | undefined => {
  const { data } = detail;

  const filteredEvent = whitelist.find(
    ({ appName, type, action }) =>
      appName === data.appName && type === detail.type && action === data.action
  );

  if (!filteredEvent || !filteredEvent.appName) {
    return undefined;
  }

  return filteredEvent
    ? `${setKebabCase(filteredEvent.appName)}::task::${filteredEvent.type}::${
        filteredEvent.action
      }`
    : undefined;
};

// Get matching event via regex
export const getNavigationRegexMatch = (
  whitelist: NavigationType[],
  data: INavigationData,
  currentUri: string
): NavigationType | undefined => {
  try {
    return whitelist.find(
      ({ appName, uri }) =>
        appName === data.appName &&
        !!uri.regex &&
        currentUri.match(new RegExp(uri.regex))
    );
  } catch {
    return undefined;
  }
};

// Filter on:
// event.detail.data.currentUri
// event.detail.data.appName
// return <appName>::navigation::<currentUri>
export const getNavigationEvent = (
  environment: EnvironmentNames,
  data: INavigationData,
  whitelist: NavigationType[]
): string | undefined => {
  if (!data.currentUri) {
    return undefined;
  }

  // If Staging update to check against a Prod version of the currentUri
  const domainCorrectedCurrentUri = formatUri(environment, data.currentUri);

  const filteredLabelEvent = whitelist.find(
    ({ appName, uri }) =>
      appName === data.appName && uri.label === domainCorrectedCurrentUri
  );

  const filteredRegexEvent = getNavigationRegexMatch(
    whitelist,
    data,
    domainCorrectedCurrentUri
  );

  if (filteredLabelEvent) {
    return `${setKebabCase(filteredLabelEvent.appName)}::navigation::${
      filteredLabelEvent.uri.label
    }`;
  } else if (filteredRegexEvent) {
    return `${setKebabCase(filteredRegexEvent.appName)}::navigation::${
      filteredRegexEvent.uri.label
    }`;
  } else {
    return undefined;
  }
};

// Filter on:
// event.detail.data.appName
// event.detail.type
// event.detail.path
// <app-name>::navigationCard::originenergy<path>
export const getNavigationCardEvent = (
  data: INavigationCardData,
  whitelist: NavigationCardType[]
): string | undefined => {
  const filteredEvent = whitelist.find(
    ({ appName, path }) => appName === data.appName && path === data.path
  );

  if (!filteredEvent || !filteredEvent.appName) {
    return undefined;
  }

  return filteredEvent
    ? `${setKebabCase(filteredEvent.appName)}::navigationCard::originenergy${
        filteredEvent.path
      }`
    : undefined;
};

// Filter on:
// event.detail.data.appName
// event.detail.type
// event.details.data.id
// <app-name>::interaction::<type>::<id>
export const getInteractionEvent = (
  detail: {
    eventType: AnalyticsEventType;
    interaction: IInteractionTypes;
    data: IInteractionData;
  },
  whitelist: InteractionType[]
): string | undefined => {
  const { data } = detail;

  const filteredEvent = whitelist.find(
    ({ appName, componentType, id }) =>
      appName === data.appName &&
      id === setKebabCase(data.id) &&
      componentType === data.componentType.toLowerCase()
  );

  if (!filteredEvent || !filteredEvent.appName) {
    return undefined;
  }

  return filteredEvent
    ? `${setKebabCase(
        filteredEvent.appName
      )}::interaction::${filteredEvent.componentType.toLowerCase()}::${setKebabCase(
        filteredEvent.id
      )}`
    : undefined;
};

export const getTrackingEventName = (
  environment: EnvironmentNames,
  detail:
    | InteractionDetailType
    | NavigationDetailType
    | NavigationCardDetailType
    | TaskDetailType
): string | undefined => {
  // white listed events (navigation, task, interaction)
  const getBrazeEvent = (eventType: string | undefined) => {
    const { events } = WHITE_LIST_BRAZE;

    switch (eventType) {
      case "interaction":
        return getInteractionEvent(
          detail as InteractionDetailType,
          events["interaction"]
        );

      case "navigation":
        return getNavigationEvent(
          environment,
          detail.data as INavigationData,
          events["navigation"]
        );

      case "navigationCard":
        return getNavigationCardEvent(
          detail.data as INavigationCardData,
          events["navigationCard"]
        );

      case "task":
        return getTaskEvent(detail as TaskDetailType, events["task"]);

      default:
        return undefined;
    }
  };

  return getBrazeEvent(detail?.eventType);
};

let checkedUser = false;

export const handleEventListener = (
  environment: EnvironmentNames,
  event: any,
  logCustomEvent: (
    brazeEventName: string,
    data: INavigationData | ITaskData
  ) => void
) => {
  const { detail } = event;
  // check if it is whitelisted
  const brazeEventName = getTrackingEventName(environment, detail);

  if (brazeEventName) {
    const { data } = detail;
    if (checkedUser) {
      // confirmed user account and auth?
      logCustomEvent(brazeEventName, data);
    } else {
      // Queued events -
      addBrazeEventsToBeSent({
        brazeEventName,
        data,
      });
    }
  }
};

export const sendBrazeEvents = (
  logCustomEvent: (
    brazeEventName: string,
    data: INavigationData | ITaskData
  ) => void
) => {
  checkedUser = true;
  brazeEventsToBeSent.forEach((event: BrazeEventType) => {
    const { brazeEventName, data } = event;
    logCustomEvent(brazeEventName, data);
  });
  // clear queue
  brazeEventsToBeSent = [];
};
